import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Link,
  Spinner,
  Text,
  Title,
} from '@nimbus-ds/components';
import ResolvedInvoiceStatusDto from '@tiendanube/common/src/domains/orders/commons/ResolvedInvoiceStatusDto';
import { ResolvedInvoiceStatus } from '@tiendanube/common/src/enums';
import { useOrderInvoices } from 'domains/Orders/Orders/hooks';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import Invoices from './Invoices';

interface CardInvoiceProps {
  orderId: string;
  fulfillmentOrderId: string;
  hasCompleteCustomerInfo: boolean;
  isValidInvoiceStatus: boolean;
  onInvoiceRequestSuccess: () => void;
  onInvoiceError: () => void;
}

function CardInvoice({
  orderId,
  fulfillmentOrderId,
  hasCompleteCustomerInfo,
  isValidInvoiceStatus,
  onInvoiceRequestSuccess,
  onInvoiceError,
}: CardInvoiceProps) {
  const t = useTranslationOrders();

  const isInvoiceIssueAvailable =
    isValidInvoiceStatus && hasCompleteCustomerInfo;

  const [invoiceStatus, setInvoiceStatus] =
    useState<ResolvedInvoiceStatusDto | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { handleInvoiceSubmission, getResolvedInvoices } = useOrderInvoices({
    orderId,
    fulfillmentOrderId,
    onInvoiceRequestSuccess: async () => {
      await fetchResolvedInvoices();
      onInvoiceRequestSuccess();
    },
    onInvoiceError,
  });

  const fetchResolvedInvoices = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await getResolvedInvoices();
      setInvoiceStatus(result);
    } finally {
      setIsLoading(false);
    }
  }, [getResolvedInvoices]);

  useEffect(() => {
    fetchResolvedInvoices();
  }, [fetchResolvedInvoices]);

  const status = invoiceStatus?.status;
  const isMissingCustomerInfo = !hasCompleteCustomerInfo;
  const isUnpacked = !isValidInvoiceStatus;

  const shouldShowButton = useMemo(() => {
    if (isMissingCustomerInfo || isUnpacked) return false;

    switch (status) {
      case ResolvedInvoiceStatus.SHOW_DOCUMENTS:
      case ResolvedInvoiceStatus.EMISSION_IN_PROGRESS:
      case ResolvedInvoiceStatus.CERT_ERROR:
        return false;
      case ResolvedInvoiceStatus.ALL_CANCELLED:
      case ResolvedInvoiceStatus.ERROR_ON_EMIT:
      case ResolvedInvoiceStatus.EMPTY_DOCUMENTS:
        return true;
      default:
        return isInvoiceIssueAvailable;
    }
  }, [status, isMissingCustomerInfo, isUnpacked, isInvoiceIssueAvailable]);

  const shouldShowDocumentsError = [
    ResolvedInvoiceStatus.ERROR_ON_EMIT,
  ].includes(status as ResolvedInvoiceStatus);

  const handleButtonClick = () => {
    setIsLoading(true);
    handleInvoiceSubmission();
  };

  const customMessage = useMemo(() => {
    switch (status) {
      case ResolvedInvoiceStatus.EMISSION_IN_PROGRESS:
        return t('cardInvoice.issued');
      case ResolvedInvoiceStatus.CERT_ERROR:
        return (
          <>
            {t('cardInvoice.digitalCertificateConfig.message')}{' '}
            <Link
              as="a"
              href="/settings/invoices"
              target="_blank"
              appearance="primary"
              textDecoration="none"
            >
              {t('cardInvoice.digitalCertificateConfig.link')}
            </Link>
          </>
        );
      default:
        return null;
    }
  }, [status, t]);

  const resolvedErrors = invoiceStatus?.errors ?? [];

  return (
    <Card>
      <Card.Header>
        <Title as="h4">{t('cardInvoice.title')}</Title>
      </Card.Header>
      {invoiceStatus?.invoices && (
        <Invoices invoices={invoiceStatus.invoices} t={t} />
      )}
      {shouldShowDocumentsError && (
        <Box marginTop="2">
          <Alert
            title={t('cardInvoice.alert.errorRequest.title')}
            appearance="danger"
            show
          >
            {resolvedErrors.length > 0 &&
              resolvedErrors.map((errorGroup, index) => (
                <Box key={`${index}-${errorGroup.error_code}`} marginBottom="2">
                  {errorGroup.errors.map((errMsg, subIndex) => (
                    <Text
                      key={`${index}-${subIndex}`}
                      fontSize="base"
                      fontWeight="regular"
                    >
                      {errMsg}
                    </Text>
                  ))}
                </Box>
              ))}
          </Alert>
        </Box>
      )}
      {status !== undefined && shouldShowButton && (
        <Box display="flex" flexDirection="row" flex="1">
          <Button
            appearance="neutral"
            onClick={handleButtonClick}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Spinner color="currentColor" size="small" />
                {t('cardInvoice.button.loading')}
              </>
            ) : (
              t('cardInvoice.button.issue')
            )}
          </Button>
        </Box>
      )}

      {!!customMessage && (
        <Box marginTop="2">
          <Text fontSize="base" color="neutral-textLow">
            {customMessage}
          </Text>
        </Box>
      )}

      {!customMessage && isUnpacked && (
        <Text fontSize="base" color="neutral-textLow">
          {t('cardInvoice.unpacked')}
        </Text>
      )}

      {!customMessage && isMissingCustomerInfo && (
        <Alert
          title={t('cardInvoice.alert.missingCustomerInfo.title')}
          appearance="warning"
          show
        >
          <Text fontSize="base" fontWeight="regular">
            {t('cardInvoice.alert.missingCustomerInfo.message')}
          </Text>
        </Alert>
      )}
    </Card>
  );
}

export default CardInvoice;
